import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppointmentType } from '../../types';
import { PatientTypeEnum } from '../../types';

export type PatientType = PatientTypeEnum | '';

export interface PatientDetails {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  notes?: string;
  id? : string;
  external_resource_id? : string;
  birth_date? : string;
}

export const createEmptyPatientDetails = (): PatientDetails => ({
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  notes: '',
  birth_date: '',
});

interface AppointmentState {
  treatment: AppointmentType | null;
  patientType: PatientType;
  selectedSlot: string | null;
  patientDetails: PatientDetails;
  selectedPmsOperatoryId?: string | null;
  selectedDurationMinutes?: number | null;
  selectedOperatories?: string[];
  patient_request_id: string | null;
}

const initialState: AppointmentState = {
  treatment: null,
  patientType: '',
  selectedSlot: null,
  patientDetails: createEmptyPatientDetails(),
  selectedPmsOperatoryId: null,
  selectedDurationMinutes: null,
  selectedOperatories: [],
  patient_request_id: null,
};

const appointmentSlice = createSlice({
  name: 'appointment',
  initialState,
  reducers: {
    setTreatment(state, action: PayloadAction<AppointmentType>) {
      state.treatment = action.payload;
      // Reset downstream selections when treatment changes
      state.selectedSlot = null;
      state.selectedPmsOperatoryId = null;
      // Recompute duration if patient type already chosen
      if (state.patientType && action.payload) {
        state.selectedDurationMinutes =
          state.patientType === 'new'
            ? action.payload.newPatientMinutes
            : action.payload.existingPatientMinutes;
      } else {
        state.selectedDurationMinutes = null;
      }
    },
    setPatientRequestId: (state, action: PayloadAction<string>) => {
      state.patient_request_id = action.payload;
    },
    setPatientType(state, action: PayloadAction<PatientType>) {
      state.patientType = action.payload;
      if (state.treatment) {
        state.selectedDurationMinutes =
          action.payload === 'new'
            ? state.treatment.newPatientMinutes
            : state.treatment.existingPatientMinutes;
      } else {
        state.selectedDurationMinutes = null;
      }
    },
    setSelectedSlot(state, action: PayloadAction<string | null>) {
      state.selectedSlot = action.payload;
    },
    setSelectedPmsOperatoryId(state, action: PayloadAction<string | null>) {
      state.selectedPmsOperatoryId = action.payload;
    },
    setSelectedOperatories(state, action: PayloadAction<string[]>) {
      state.selectedOperatories = action.payload
    },
    setPatientDetails(state, action: PayloadAction<PatientDetails>) {
      state.patientDetails = action.payload;
    },
    resetAppointment(state) {
      state.treatment = null;
      state.patientType = '';
      state.selectedSlot = null;
      state.patientDetails = createEmptyPatientDetails();
      state.selectedPmsOperatoryId = null;
      state.selectedDurationMinutes = null;
      state.selectedOperatories = [];
    },
  },
});

export const {
  setTreatment,
  setPatientType,
  setSelectedSlot,
  setSelectedPmsOperatoryId,
  setSelectedOperatories,
  setPatientDetails,
  setPatientRequestId,
  resetAppointment,
} = appointmentSlice.actions;

export default appointmentSlice.reducer;





